import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Mode } from '../theme'

/**
 * The sticky bar: brand, the tabs, search, and the small buttons.
 *
 * Every tab is its own path (/recipes, /sisters), so a member can bookmark the recipes or the board. On a narrow screen the
 * same tabs drop under the bar as a scrolling row rather than into a hamburger; six links fit.
 */

export type View =
  | 'home'
  | 'table'
  | 'sisters'
  | 'calendar'
  | 'account'
  | 'phone'
  | 'keepers'
  | 'privacy'
  | 'signin'

/** The path each view lives at, relative to the router's basename. */
export const VIEW_PATH: Record<View, string> = {
  home: '/',
  table: '/recipes',
  sisters: '/sisters',
  calendar: '/calendar',
  account: '/account',
  phone: '/phone',
  keepers: '/keepers',
  privacy: '/privacy',
  signin: '/signin',
}

export function viewOf(pathname: string): View | null {
  const clean = pathname.replace(/\/+$/, '') || '/'
  if (clean === '/register') return 'signin'
  if (clean === '/table') return 'table' // the old path; App.tsx moves it to /recipes
  if (clean === '/privacy.html') return 'privacy'
  if (clean.startsWith('/sisters/')) return 'sisters' // the forum's own screens
  const found = (Object.keys(VIEW_PATH) as View[]).find((view) => VIEW_PATH[view] === clean)
  return found ?? null
}

const TABS: { view: View; label: string; icon: string; tag?: string }[] = [
  { view: 'home', label: 'Home', icon: 'ph-house' },
  { view: 'table', label: 'Recipes', icon: 'ph-bowl-food' },
  { view: 'sisters', label: 'Sisters', icon: 'ph-hands-praying' },
  { view: 'calendar', label: 'Calendar', icon: 'ph-calendar-blank' },
  { view: 'phone', label: 'My phone', icon: 'ph-device-mobile', tag: 'Private' },
  { view: 'account', label: 'Account', icon: 'ph-user-circle' },
]

const PUBLIC_TABS: { view: View; label: string; icon: string }[] = [
  { view: 'home', label: 'What it is', icon: 'ph-flower-lotus' },
  { view: 'table', label: 'Recipes', icon: 'ph-bowl-food' },
  { view: 'keepers', label: 'The Keepers', icon: 'ph-seal-check' },
  { view: 'privacy', label: 'Privacy', icon: 'ph-lock-key' },
]

export function TopBar({
  view,
  signedIn,
  initial,
  mode,
  onMode,
  onSearch,
  onAdd,
  onSignOut,
}: {
  view: View
  signedIn: boolean
  initial: string
  mode: Mode
  onMode: (mode: Mode) => void
  onSearch?: () => void
  onAdd?: () => void
  onSignOut?: () => void
}) {
  const dark = mode === 'dark'
  const [menu, setMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Closes on a click anywhere else, on Escape, and on following a link in it.
  useEffect(() => {
    if (!menu) return
    const onDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenu(false)
    }
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setMenu(false)
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [menu])

  const tabs = TABS.map((tab) => (
    <Link
      key={tab.view}
      to={VIEW_PATH[tab.view]}
      className={view === tab.view ? 'nav-link is-active' : 'nav-link'}
      aria-current={view === tab.view ? 'page' : undefined}
    >
      <i className={`${view === tab.view ? 'ph-fill' : 'ph-duotone'} ${tab.icon}`} aria-hidden="true" />
      <span>{tab.label}</span>
      {tab.tag && <span className="nav-tag">{tab.tag}</span>}
    </Link>
  ))

  return (
    <header className="topbar">
      <div className="wrap">
        <div className="topbar-inner">
          <div className="topbar-left">
            <Link className="brand" to={VIEW_PATH.home} aria-label="Covellah home">
              <span className="brand-tile">
                <i className="ph-bold ph-flower-lotus" aria-hidden="true" />
              </span>
              <span>
                <span className="brand-name">COVELLAH</span>
              </span>
            </Link>
            {signedIn ? (
              <nav className="nav" aria-label="Site">
                {tabs}
              </nav>
            ) : (
              <nav className="nav" aria-label="Site">
                {PUBLIC_TABS.map((tab) => (
                  <Link
                    key={tab.view}
                    className={view === tab.view ? 'nav-link is-active' : 'nav-link'}
                    to={VIEW_PATH[tab.view]}
                    aria-current={view === tab.view ? 'page' : undefined}
                  >
                    <i
                      className={`${view === tab.view ? 'ph-fill' : 'ph-duotone'} ${tab.icon}`}
                      aria-hidden="true"
                    />
                    <span>{tab.label}</span>
                  </Link>
                ))}
              </nav>
            )}
          </div>

          <div className="topbar-right">
            {signedIn && onSearch && (
              <button className="omni" type="button" onClick={onSearch}>
                <i className="ph-bold ph-magnifying-glass" aria-hidden="true" />
                <span>Search recipes, prayers, ingredients</span>
                <kbd className="kbd">Ctrl K</kbd>
              </button>
            )}
            {signedIn && onAdd && (
              <button className="icon-btn" type="button" title="Share a recipe" onClick={onAdd}>
                <i className="ph-bold ph-plus" aria-hidden="true" />
              </button>
            )}
            <button
              className="icon-btn is-gold"
              type="button"
              title={dark ? 'Switch to light' : 'Switch to dark'}
              onClick={() => onMode(dark ? 'light' : 'dark')}
            >
              <i className={dark ? 'ph-bold ph-sun' : 'ph-bold ph-moon-stars'} aria-hidden="true" />
            </button>
            {signedIn ? (
              <div className="avatar-wrap" ref={menuRef}>
                <button
                  className="avatar"
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={menu}
                  aria-label="Your account"
                  onClick={() => setMenu((open) => !open)}
                >
                  {initial}
                </button>
                {menu && (
                  <div className="menu" role="menu">
                    <Link role="menuitem" to={VIEW_PATH.account} onClick={() => setMenu(false)}>
                      <i className="ph-bold ph-user-circle" aria-hidden="true" /> Account
                    </Link>
                    <Link role="menuitem" to={VIEW_PATH.phone} onClick={() => setMenu(false)}>
                      <i className="ph-bold ph-device-mobile" aria-hidden="true" /> My phone
                    </Link>
                    <Link role="menuitem" to={VIEW_PATH.privacy} onClick={() => setMenu(false)}>
                      <i className="ph-bold ph-lock-key" aria-hidden="true" /> Privacy policy
                    </Link>
                    <hr />
                    <button
                      role="menuitem"
                      type="button"
                      className="is-danger"
                      onClick={() => {
                        setMenu(false)
                        onSignOut?.()
                      }}
                    >
                      <i className="ph-bold ph-sign-out" aria-hidden="true" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link className="btn-primary btn-sm" to={VIEW_PATH.signin}>
                Sign in
              </Link>
            )}
          </div>
        </div>
        {signedIn && (
          <nav className="nav-row" aria-label="Site">
            {tabs}
          </nav>
        )}
      </div>
    </header>
  )
}
